import { memo } from "preact/compat";
import { useRef } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { Checkbox, Typography } from "../../shared/ui";
import {
  formatMinOneDecimal,
  normalizeDecimalInput,
  clamp,
} from "../../shared/utils/formatNumber";
import css from "./ControlPanel.module.scss";

interface AutoCashoutState {
  enabled: boolean;
  value: number | string | null;
  lastValid: number;
}

interface AutoCashOutSectionProps {
  autoCashout: AutoCashoutState;
  isDisabled: boolean;
  onToggle: (enabled: boolean) => void;
  onValueChange: (patch: Partial<AutoCashoutState>) => void;
  onCommit: (level: number) => void;
  showError: (message: string) => void;
}

const MIN_CASHOUT = 1.01;
const MAX_CASHOUT = 100;

export const AutoCashOutSection = memo(function AutoCashOutSection({
  autoCashout,
  isDisabled,
  onToggle,
  onValueChange,
  onCommit,
  showError,
}: AutoCashOutSectionProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleToggle = (checked: boolean) => {
    if (isDisabled) {
      showError(t("main.cannot_change_auto_cashout"));
      return;
    }
    const level = checked ? getCurrentLevel() : 0;
    onCommit(level);
  };

  const getCurrentLevel = (): number => {
    if (typeof autoCashout.value === "number") return autoCashout.value;
    return (
      parseFloat(String(autoCashout.value ?? "").replace(/,/g, ".")) ||
      autoCashout.lastValid
    );
  };

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const normalized = normalizeDecimalInput(target.value);
    onValueChange({ value: normalized });
  };

  const handleBlur = () => {
    const normalized = String(autoCashout.value ?? "").replace(/,/g, ".");
    const numValue = parseFloat(normalized);

    let finalValue: number;
    if (isNaN(numValue) || numValue < MIN_CASHOUT) {
      finalValue = Math.max(autoCashout.lastValid, MIN_CASHOUT);
    } else {
      finalValue = clamp(
        Math.round(numValue * 100) / 100,
        MIN_CASHOUT,
        MAX_CASHOUT
      );
    }

    onValueChange({
      value: formatMinOneDecimal(finalValue),
      lastValid: finalValue,
    });

    if (autoCashout.enabled && !isDisabled) {
      onCommit(finalValue);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const invalidChars = ["e", "E", "+", "-"];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
      return;
    }
    if (event.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  const handlePaste = (event: ClipboardEvent) => {
    const text = event.clipboardData?.getData("text") ?? "";
    if (!/^\d*[.,]?\d{0,2}$/.test(text)) {
      event.preventDefault();
    }
  };

  const isInputDisabled = isDisabled || !autoCashout.enabled;

  return (
    <div className={css.autoCashOutSection}>
      <Checkbox
        checked={autoCashout.enabled}
        onChange={handleToggle}
        disabled={isDisabled}
        variant="border"
      />
      <p
        className={`${css.autoCashOutLabel} ${autoCashout.enabled ? css.active : ""}`}
      >
        {t("main.auto_cash_out")}
      </p>
      <div className={css.cashOutValue}>
        <Typography
          className={`${css.multiplierSuffix} ${autoCashout.enabled ? css.active : ""}`}
          variant="caption1"
        >
          x
        </Typography>
        <input
          ref={inputRef}
          disabled={isInputDisabled}
          className={css.multiplierInput}
          type="text"
          inputmode="decimal"
          pattern="^[0-9]*[\.,]?[0-9]{0,2}$"
          value={autoCashout.value ?? ""}
          onInput={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          max={MAX_CASHOUT}
          step={0.01}
          placeholder="1.1"
        />
      </div>
    </div>
  );
});
